"use client";

import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Fade,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { ErpContext } from "../../context/ErpContext.js";
import { formatNumberForDisplay, getLocaleConfig } from "../../utils/numeros.js";
import ModalCrearActualizarEntidad from "./ModalCrearActualizarEntidad";
import ModalDocumentosAdjuntos from "./ModalDocumentosAdjuntos";
import CustomPagination from "../transaccionComponent/CustomPagination";
import Swal from "sweetalert2";

const TablaEntidadProveedor = ({ embedded = false }) => {
  const theme = useTheme();
  const { 
    getEntidadFilterTipoEntidad,
    getEntidadByID,
    isNavbarCollapsed,
    formatoNumericoGlobal,
    verificarEliminacionCliente,
    deleteEntidadCliente,
    user,
    tieneAccesoFuncionalidad,
  } = useContext(ErpContext);

  // Permisos granulares (CLIENTES3 - Tab Clientes)
  const mostrarBtnEditarCliente = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_btnEditarCliente");
  const mostrarBtnEliminarCliente = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_btnEliminarCliente");

  // Configuración de locale para formateo numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  );

  // Estados
  const [Clientes, setProveedores] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [openModalDocumentos, setOpenModalDocumentos] = useState(false);
  const [adjuntosDocumentos, setAdjuntosDocumentos] = useState(null);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Paginación del servidor
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros individuales
  const [filters, setFilters] = useState({
    searchId: '',
    searchNombre: '',
    searchApellido: '',
    searchDni: '',
  });

  // Refs para debounce
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);

  // Obtener Clientes con paginación del servidor
  const fetchClientes = async (showSearchingAnimation = false) => {
    if (showSearchingAnimation) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await getEntidadFilterTipoEntidad(1, page, 100, filters);
      
      // Manejar respuesta con paginación
      if (response.data) {
        setProveedores(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.total);
      } else {
        // Retrocompatibilidad
        setProveedores(response);
        setTotalCount(response.length);
      }
    } catch (err) {
      console.error("Error al cargar Clientes:", err);
      setError("No se pudieron cargar los Clientes. Intente nuevamente.");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // useEffect para carga inicial
  useEffect(() => {
    fetchClientes(false);
    isInitialMount.current = false;
  }, []);

  // useEffect para cambios de página (sin debounce, sin animación)
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchClientes(false);
    }
  }, [page]);

  // useEffect con debounce para filtros (permite seguir escribiendo)
  useEffect(() => {
    // No hacer nada en la primera carga
    if (isInitialMount.current) {
      return;
    }

    // Limpiar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Si hay filtros activos, mostrar animación inmediatamente
    const hasActiveFilters = filters.searchId || filters.searchNombre || 
                            filters.searchApellido || filters.searchDni;
    if (hasActiveFilters) {
      setIsSearching(true);
    }

    // Configurar debounce de 500ms para la búsqueda
    debounceTimerRef.current = setTimeout(() => {
      setPage(1); // Reset a página 1 cuando cambian los filtros
      fetchClientes(true);
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);

  // Abrir modal para EDITAR cliente
  const handleOpenEditModal = (cliente) => {
    setSelectedProveedor(cliente); // Cargar datos en el modal
    setOpenModal(true);
  };

  // Abrir modal para CREAR cliente
  const handleOpenCreateModal = () => {
    setSelectedProveedor({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 2, // Tipo entidad = Cliente
    });
    setOpenModal(true);
  };

  // Cerrar modal y actualizar lista si se guardaron cambios
  const handleCloseModal = async (refresh = false) => {
    setOpenModal(false);
    if (refresh) {
      await fetchClientes();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  const handleAbrirDocumentos = async (cliente) => {
    setOpenModalDocumentos(true);
    setLoadingDocumentos(true);
    try {
      const entidad = await getEntidadByID(cliente.id);
      setAdjuntosDocumentos(entidad?.adjuntos || null);
    } catch (error) {
      console.error("Error al cargar documentos adjuntos:", error);
      setAdjuntosDocumentos(null);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Manejar eliminación de cliente con validaciones
  const handleDeleteCliente = async (cliente) => {
    try {
      // Verificar si puede eliminar
      const verificacion = await verificarEliminacionCliente(cliente.id);
      
      if (!verificacion.puedeEliminar) {
        // Construir HTML para mostrar los paquetes
        let paquetesHtml = '<div style="text-align: left; margin-top: 15px;">';
        paquetesHtml += '<strong>Paquetes asociados:</strong><br/>';
        
        verificacion.paquetes.forEach((paq, index) => {
          paquetesHtml += `
            <div style="margin: 10px 0; padding: 10px; background: #f5f5f5; border-radius: 5px;">
              <strong>Paquete ${index + 1}:</strong><br/>
              <strong>Grupo:</strong> ${paq.grupo}<br/>
              <strong>Destino:</strong> ${paq.destino}<br/>
              <strong>Fechas:</strong> ${new Date(paq.fechaInicio).toLocaleDateString('es-AR')} - ${new Date(paq.fechaFin).toLocaleDateString('es-AR')}<br/>
              <strong>Cuotas pagadas:</strong> ${paq.cantidadCuotasPagadas}
            </div>
          `;
        });
        paquetesHtml += '</div>';
        
        // Modal de error con información de paquetes
        await Swal.fire({
          icon: 'error',
          title: 'No se puede eliminar el cliente',
          html: `
            <p>${verificacion.mensaje}</p>
            ${paquetesHtml}
          `,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#d33',
        });
        return;
      }
      
      // Si puede eliminar, mostrar confirmación
      const result = await Swal.fire({
        icon: 'warning',
        title: '¿Está seguro?',
        html: `
          <p>¿Desea eliminar al cliente <strong>${cliente.descripcion} ${cliente.apellido || ''}</strong>?</p>
          <p style="color: #999; font-size: 14px;">Esta acción no se puede deshacer.</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
      });
      
      if (result.isConfirmed) {
        await deleteEntidadCliente(cliente.id);
        
        await Swal.fire({
          icon: 'success',
          title: 'Cliente eliminado',
          text: 'El cliente ha sido eliminado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        
        // Recargar la lista
        fetchClientes();
      }
      
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al intentar eliminar el cliente',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const tableStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  };

  return (
    <Box sx={{ 
      height: embedded ? "100%" : "100vh",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      p: { xs: 1, sm: 2, md: 3 },
    }}>
      {/* Encabezado */}
      <Card
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: "visible",
          flexShrink: 0,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <BusinessIcon
              sx={{
                fontSize: 28,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Gestión de Clientes
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Sección de Filtros y Botones */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
              <TextField
                size="small"
                label="Buscar por ID"
                value={filters.searchId}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, searchId: e.target.value }));
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 140 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                inputProps={{
                  maxLength: 14,
                }}
              />
              <TextField
                size="small"
                label="Buscar por Nombre"
                value={filters.searchNombre}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, searchNombre: e.target.value }));
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  minWidth: { xs: "100%", sm: 150 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                size="small"
                label="Buscar por Apellido"
                value={filters.searchApellido}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, searchApellido: e.target.value }));
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  minWidth: { xs: "100%", sm: 150 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                size="small"
                label="Buscar por DNI"
                value={filters.searchDni}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, searchDni: e.target.value }));
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 140 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                inputProps={{
                  maxLength: 14,
                }}
              />
              {/* Botones de acción en la misma fila */}
              <Box sx={{ display: "flex", gap: 1, ml: { xs: 0, sm: "auto" }, flexShrink: 0, width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={fetchClientes}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    minWidth: 100,
                  }}
                >
                  Actualizar
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleOpenCreateModal}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: 2,
                    minWidth: 140,
                    backgroundColor: "#5b5b5f",
                    "&:hover": {
                      backgroundColor: alpha("#5b5b5f", 0.8),
                    },
                  }}
                >
                  Nuevo Cliente
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alertas - Solo se muestran cuando están activas */}
      {(success || error) && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          {success && (
            <Fade in={success}>
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                onClose={() => setSuccess(false)}
              >
                Operación completada con éxito
              </Alert>
            </Fade>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                boxShadow: 1,
                mt: success ? 1 : 0,
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Box>
      )}

      {/* Tabla de Clientes */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {/* Overlay de carga inicial */}
          {loading && !isSearching && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Loading de búsqueda (solo en el área de la tabla) */}
          {isSearching && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 11,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}

          <TableContainer
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "auto",
              minHeight: 0,
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Apellido
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                      textAlign: "left",
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    DNI / CUIT / CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Observaciones
                  </TableCell>
                  {(mostrarBtnEditarCliente || mostrarBtnEliminarCliente) && (
                    <TableCell
                      align="left"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#5b5b5f",
                        color: "white",
                        width: "10%",
                        padding: "0 16px",
                        height: "45px !important",
                        lineHeight: "45px",
                      }}
                    >
                      Acciones
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Clientes.length > 0 ? (
                  Clientes.map((prov) => (
                    <TableRow
                      key={prov.id}
                      hover
                      sx={{
                        height: "35px",
                        "&:nth-of-type(even)": {
                          backgroundColor: "#F7F7F7",
                        },
                        "&:hover": {
                          backgroundColor: "#E3EAFD",
                          transition: "background 0.3s ease",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={prov.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            minWidth: 40,
                            justifyContent: "center",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                          textAlign: "left !important",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon
                            fontSize="small"
                            sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {prov.apellido || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                          textAlign: "left !important",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {prov.descripcion || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BadgeIcon
                            fontSize="small"
                            sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                          />
                          <Typography variant="body2">
                            {prov.dniCuitCuil 
                              ? formatNumberForDisplay(parseInt(prov.dniCuitCuil, 10), { locale: localeConfig.locale }).split(',')[0]
                              : "—"
                            }
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {prov.email && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <EmailIcon
                                fontSize="small"
                                sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                              />
                              <Typography variant="body2" noWrap>
                                {prov.email}
                              </Typography>
                            </Box>
                          )}
                          {prov.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon
                                fontSize="small"
                                sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                              />
                              <Typography variant="body2">
                                {prov.telefono}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        {prov.observaciones ? (
                          <Tooltip title={prov.observaciones} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {prov.observaciones}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#999" }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {(mostrarBtnEditarCliente || mostrarBtnEliminarCliente) && (
                      <TableCell
                        align="left"
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="Documentos adjuntos">
                            <IconButton
                              onClick={() => handleAbrirDocumentos(prov)}
                              size="small"
                              sx={{
                                color: alpha("#5b5b5f", 0.9),
                                "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
                              }}
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {mostrarBtnEditarCliente && (
                            <Tooltip title="Editar cliente">
                              <IconButton
                                onClick={() => handleOpenEditModal(prov)}
                                color="primary"
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                    ),
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {mostrarBtnEliminarCliente && (
                            <Tooltip title="Eliminar cliente">
                              <IconButton
                                onClick={() => handleDeleteCliente(prov)}
                                color="error"
                                size="small"
                                sx={{
                                  ml: mostrarBtnEditarCliente ? 1 : 0,
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    0.1
                                  ),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.error.main,
                                      0.2
                                    ),
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={mostrarBtnEditarCliente || mostrarBtnEliminarCliente ? 7 : 6} align="center" sx={{ py: 3 }}>
                      {loading ? (
                        <Typography color="text.secondary">
                          Cargando Clientes...
                        </Typography>
                      ) : (filters.searchId || filters.searchNombre || filters.searchApellido || filters.searchDni) ? (
                        <Typography color="text.secondary">
                          No se encontraron Clientes con ese criterio de
                          búsqueda
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">
                          No hay Clientes registrados
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* CustomPagination */}
          <Box sx={{ 
            flexShrink: 0, 
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(0,0,0,0.08)",
            padding: "0px",
            paddingBottom: "10px",
          }}>
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              vieneDe={"tablaEntidades"}
              onPageChange={(newPage) => setPage(newPage)}
            />
          </Box>
        </Box>
      </Card>

      {/* Modal de Crear / Editar Cliente */}
      {openModal && (
        <ModalCrearActualizarEntidad
          open={openModal}
          handleClose={handleCloseModal}
          entidad={selectedProveedor}
          idTipoEntidad={1}
        />
      )}

      {/* Modal de documentos adjuntos */}
      <ModalDocumentosAdjuntos
        open={openModalDocumentos}
        handleClose={() => {
          setOpenModalDocumentos(false);
          setAdjuntosDocumentos(null);
        }}
        adjuntosJson={adjuntosDocumentos}
        loading={loadingDocumentos}
      />
    </Box>
  );
};

export default TablaEntidadProveedor;
